import { CommonModule, DatePipe, NgForOf, NgIf } from "@angular/common";
import { Component, inject, OnInit } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AuthService } from "../../core/services/auth.service";
import { ActivatedRoute, Router, RouterLink } from "@angular/router";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiDataList,
  TuiDialogService,
  TuiDropdown,
  TuiLink,
  TuiSizeL,
  TuiSizeS,
  TuiTextfield,
} from "@taiga-ui/core";
import {
  TuiBadgedContent,
  TuiBreadcrumbs,
  TuiDataListDropdownManager,
  TuiDataListWrapper,
  TuiFade,
  TuiItemsWithMore,
  TuiPagination,
  TuiTabs,
} from "@taiga-ui/kit";
import { TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import { TuiInputDateRangeModule, TuiInputModule } from "@taiga-ui/legacy";
import { TuiDayRange } from "@taiga-ui/cdk";
import { forkJoin } from "rxjs";
import { SalaryConfigService } from "../../core/services/salary-config.service";
import { ChargeConfigService } from "../../core/services/charge-config.service";
import { FeeService } from "../../core/services/fee.service";
import { DisableUnlessPermissionDirective } from "../../core/directives/disable-unless-permission.directive";
import { EnterSubmitDirective } from "../../core/directives/enter-submit.directive";

@Component({
  selector: 'app-salary-charge-config',
  standalone: true,
  imports: [
    DisableUnlessPermissionDirective,
    EnterSubmitDirective,
    FormsModule,
    TuiAppearance,
    TuiDataListWrapper,
    TuiDataList,
    TuiDropdown,
    TuiTable,
    TuiNavigation,
    TuiTabs,
    TuiTextfield,
    TuiItemsWithMore,
    TuiSearch,
    ReactiveFormsModule,
    CommonModule,
    TuiBadgedContent,
    TuiInputModule,
    TuiButton,
    TuiInputDateRangeModule,
  ],
  templateUrl: './salary-charge-config.component.html',
  styleUrl: './salary-charge-config.component.scss'
})
export class SalaryChargeConfigComponent implements OnInit {
  beforeSalaryForm!: FormGroup;
  afterSalaryForm!: FormGroup;
  chargeForm!: FormGroup;
  feeForm!: FormGroup;
  salaryId: string = '';
  chargeId: string = '';
  isLoading: boolean = false;
  loggedInUserCanEdit = false;

  isSalaryBeforeSaving: boolean = false;
  isSalaryAfterSaving: boolean = false;
  isChargesSaving: boolean = false;
  feeLoading: boolean = false;
  private authService = inject(AuthService);

  constructor(
    private fb: FormBuilder,
    private salaryConfigService: SalaryConfigService,
    private chargeConfigService: ChargeConfigService,
    private feeService: FeeService,
    private alerts: TuiAlertService,
  ) { }

  ngOnInit(): void {
    this.initializeForms();
    this.loadSalaryConfig();
    this.loadChargeConfig();
    this.loadFeeConfig();
    this.checkUserPermission();
  }

  initializeForms() {
    this.beforeSalaryForm = this.fb.group({
      beforeYear12hrNormalWithMeal: [0],
      beforeYear12hrNormalWithoutMeal: [0],
      beforeYear12hrBabycareWithMeal: [0],
      beforeYear12hrBabycareWithoutMeal: [0],
      beforeYear12hrNHPWithMeal: [0],
      beforeYear12hrNHPWithoutMeal: [0],
      beforeYear24hrNormalWithMeal: [0],
      beforeYear24hrBabycareWithMeal: [0],
      beforeYear24hrNHPWithMeal: [0],
    });

    this.afterSalaryForm = this.fb.group({
      afterYear12hrNormalWithMeal: [0],
      afterYear12hrNormalWithoutMeal: [0],
      afterYear12hrBabycareWithMeal: [0],
      afterYear12hrBabycareWithoutMeal: [0],
      afterYear12hrNHPWithMeal: [0],
      afterYear12hrNHPWithoutMeal: [0],
      afterYear24hrNormalWithMeal: [0],
      afterYear24hrBabycareWithMeal: [0],
      afterYear24hrNHPWithMeal: [0],
    });

    this.chargeForm = this.fb.group({
      twelveHourNormalWithMeal: [0],
      twelveHourNormalWithoutMeal: [0],
      twelveHourBabycareWithMeal: [0],
      twelveHourBabycareWithoutMeal: [0],
      twelveHourNHPWithMeal: [0],
      twelveHourNHPWithoutMeal: [0],
      twentyFourHourNormalWithMeal: [0],
      twentyFourHourBabycareWithMeal: [0],
      twentyFourHourNHPWithMeal: [0],
    });

    this.feeForm = this.fb.group({
      registrationFee: [0],
      renewalFee: [0],
      lifetimeRegistrationFee: [0],
    });
  }

  loadSalaryConfig() {
    this.salaryConfigService.getAllSalaryConfig().subscribe((res: any) => {
      if (res.success && res.data && res.data.length) {
        const config = res.data[0];
        this.salaryId = config._id;
        // Patch before 1 year form
        this.beforeSalaryForm.patchValue({
          beforeYear12hrNormalWithMeal: config.beforeYear12hrNormalWithMeal,
          beforeYear12hrNormalWithoutMeal: config.beforeYear12hrNormalWithoutMeal,
          beforeYear12hrBabycareWithMeal: config.beforeYear12hrBabycareWithMeal,
          beforeYear12hrBabycareWithoutMeal: config.beforeYear12hrBabycareWithoutMeal,
          beforeYear12hrNHPWithMeal: config.beforeYear12hrNHPWithMeal,
          beforeYear12hrNHPWithoutMeal: config.beforeYear12hrNHPWithoutMeal,
          beforeYear24hrNormalWithMeal: config.beforeYear24hrNormalWithMeal,
          beforeYear24hrBabycareWithMeal: config.beforeYear24hrBabycareWithMeal,
          beforeYear24hrNHPWithMeal: config.beforeYear24hrNHPWithMeal,
        });
        // Patch after 1 year form
        this.afterSalaryForm.patchValue({
          afterYear12hrNormalWithMeal: config.afterYear12hrNormalWithMeal,
          afterYear12hrNormalWithoutMeal: config.afterYear12hrNormalWithoutMeal,
          afterYear12hrBabycareWithMeal: config.afterYear12hrBabycareWithMeal,
          afterYear12hrBabycareWithoutMeal: config.afterYear12hrBabycareWithoutMeal,
          afterYear12hrNHPWithMeal: config.afterYear12hrNHPWithMeal,
          afterYear12hrNHPWithoutMeal: config.afterYear12hrNHPWithoutMeal,
          afterYear24hrNormalWithMeal: config.afterYear24hrNormalWithMeal,
          afterYear24hrBabycareWithMeal: config.afterYear24hrBabycareWithMeal,
          afterYear24hrNHPWithMeal: config.afterYear24hrNHPWithMeal,
        });
      }
    });
  }

  loadChargeConfig() {
    this.chargeConfigService.getAllChargeConfig().subscribe((res: any) => {
      if (res.success && res.data && res.data.length) {
        const config = res.data[0];
        this.chargeId = config._id;
        this.chargeForm.patchValue({
          twelveHourNormalWithMeal: config.twelveHourNormalWithMeal,
          twelveHourNormalWithoutMeal: config.twelveHourNormalWithoutMeal,
          twelveHourBabycareWithMeal: config.twelveHourBabycareWithMeal,
          twelveHourBabycareWithoutMeal: config.twelveHourBabycareWithoutMeal,
          twelveHourNHPWithMeal: config.twelveHourNHPWithMeal,
          twelveHourNHPWithoutMeal: config.twelveHourNHPWithoutMeal,
          twentyFourHourNormalWithMeal: config.twentyFourHourNormalWithMeal,
          twentyFourHourBabycareWithMeal: config.twentyFourHourBabycareWithMeal,
          twentyFourHourNHPWithMeal: config.twentyFourHourNHPWithMeal,
        });
      }
    });
  }

  onUpdateCharge() {
     if(!this.loggedInUserCanEdit){
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.isChargesSaving = true;
    // this.isLoading = true;
    const updateData = { ...this.chargeForm.value };
    this.chargeConfigService
      .updateChargeConfig(this.chargeId, updateData)
      .subscribe((res: any) => {
        // this.isLoading = false;
        if (res) {
          this.isChargesSaving = false;
          this.alerts
            .open("Charge updated successfully", {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
        } else {
          this.isChargesSaving = false;
          this.alerts
            .open("Error updating charge", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        }
      });
  }

  onUpdateBefore() {
     if(!this.loggedInUserCanEdit){
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    // this.isLoading = true;
    this.isSalaryBeforeSaving = true;
    const updateData = { ...this.beforeSalaryForm.value };
    this.salaryConfigService
      .updateSalaryConfig(this.salaryId, updateData)
      .subscribe((res: any) => {
        // this.isLoading = false;
     
        if (res) {
             this.isSalaryBeforeSaving = false;
          this.alerts
            .open("Salary updated successfully", {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
        } else {
             this.isSalaryBeforeSaving = false;
          this.alerts
            .open("Error updating salary", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        }
      });
  }

  onUpdateAfter() {
     if(!this.loggedInUserCanEdit){
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.isSalaryAfterSaving = true;
    // Call update service for after 1 year form
    const updateData = { ...this.afterSalaryForm.value };
    this.salaryConfigService
      .updateSalaryConfig(this.salaryId, updateData)
      .subscribe((res: any) => {
        // this.isLoading = false;
        if (res) {
          this.isSalaryAfterSaving = false;
          this.alerts
            .open("Salary updated successfully", {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
        } else {
          this.isSalaryAfterSaving = true;
          this.alerts
            .open("Error updating salary", {
              label: "Error",
              appearance: "error",
            })
            .subscribe();
        }
      });
  }
  checkUserPermission(): void {
    this.loggedInUserCanEdit = this.authService.hasPermission("SALARY_CHARGES_UPDATE");
  }

  loadFeeConfig() {
    this.feeService.getCurrentFee().subscribe((res: any) => {
      if (res.success && res.data) {
        this.feeForm.patchValue({
          registrationFee: res.data.registrationFee || 0,
          renewalFee: res.data.renewalFee || 0,
          lifetimeRegistrationFee: res.data.lifetimeRegistrationFee || 0,
        });
      }
    });
  }

  submitFeeUpdate() {
    if (!this.loggedInUserCanEdit) {
      this.alerts
        .open("You do not have permission to edit.", {
          label: "Error",
          appearance: "error",
        })
        .subscribe();
      return;
    }
    this.feeLoading = true;
    const user = this.authService.getLoggedInUser();
    if (!user?._id) {
      this.feeLoading = false;
      return;
    }

    const feeData = {
      ...this.feeForm.value,
      updatedBy: user._id,
    };

    this.feeService.updateFee(feeData).subscribe({
      next: (res: any) => {
        this.feeLoading = false;
        if (res.success) {
          this.alerts
            .open("Fees updated successfully", {
              label: "Success",
              appearance: "success",
            })
            .subscribe();
        }
      },
      error: (error) => {
        this.feeLoading = false;
        this.alerts
          .open("Error updating fees", {
            label: "Error",
            appearance: "error",
          })
          .subscribe();
      },
    });
  }
}